/**
 * The Rite of Admission — the whole opening, replacing the onboarding form.
 *
 * ⚠️ THE ONE THING THAT MUST NOT BE REFACTORED CASUALLY
 *
 * `requestMotionPermission()` is called as the FIRST statement of the sigil's
 * click handler, before any `await`, and is deliberately not awaited. iOS only
 * grants motion and orientation from inside a live user gesture, and any `await`
 * before the call ends that gesture — the prompt is then rejected silently, the
 * listeners attach, no events ever fire, and every reading stays at identity.
 * That exact bug cost a field session already (HISTORY 2026-08-07).
 *
 * `getUserMedia` has the same requirement. `camera.start()` invokes it
 * synchronously before its first await, so calling it from the handler is safe —
 * but inserting an `await` ahead of it would not be.
 *
 * If a refusal happens the rite continues regardless. Every grant here has a
 * degraded path (orientation-only tracking, a camera-less field, a silent
 * Watcher), and a piece that refuses to start is worse than one that starts
 * diminished.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Sun } from '../visual/Sun/Sun.js';
import { Voice } from './Voice.js';
import { RITE, OPENING_HOLD_MS, type Beat } from './script.js';
import { requestMotionPermission } from '../spatial/DevicePose.js';
import type { CameraController } from '../passthrough/useCamera.js';
import { HAS_API } from '../config.js';

export interface RiteGrants {
  motion: boolean;
  camera: boolean;
  watcher: boolean;
}

/**
 * What happens after the last beat. Starting the session (picking a tracking
 * backend, loading WASM) takes seconds and can fail; the Rite used to just sit
 * on its final screen, or on a dead one if the start rejected.
 */
export type EntryState = 'idle' | 'loading' | 'failed';

export interface RiteProps {
  camera: CameraController;
  /** Called once the rite completes, carrying what was actually granted. */
  onEnter: (grants: RiteGrants) => void;
  entry?: EntryState;
  /** Try entering again after a failure. Not a permission gesture. */
  onRetry?: () => void;
}

export function Rite({ camera, onEnter, entry = 'idle', onRetry }: RiteProps) {
  const [index, setIndex] = useState(0);
  const [spoken, setSpoken] = useState(false);
  const [presence, setPresence] = useState(0);
  const [blinkNow, setBlinkNow] = useState(0);
  const [busy, setBusy] = useState(false);
  const grants = useRef({ motion: false, camera: false, watcher: false });

  // Beats the platform cannot honour are dropped rather than shown and refused.
  const beats = useRef<Beat[]>(RITE.filter((b) => !(b.grant === 'watcher' && !HAS_API)));
  const beat = beats.current[index]!;

  // The sun rises once, on mount, and stays.
  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    const rise = (now: number) => {
      const t = (now - t0) / 1000;
      const p = Math.min(1, t / 2.6);
      setPresence(p * p * (3 - 2 * p));
      if (p < 1) raf = requestAnimationFrame(rise);
    };
    raf = requestAnimationFrame(rise);
    return () => cancelAnimationFrame(raf);
  }, []);

  const advance = useCallback(() => {
    setSpoken(false);
    setBusy(false);
    setBlinkNow((n) => n + 1);
    if (index + 1 >= beats.current.length) {
      onEnter({ ...grants.current });
    } else {
      setIndex(index + 1);
    }
  }, [index, onEnter]);

  /** The sigil. Everything permission-related happens in this handler. */
  const touch = () => {
    if (busy) return;

    switch (beat.grant) {
      case 'motion': {
        // FIRST, synchronously, un-awaited. See the header note.
        const p = requestMotionPermission();
        setBusy(true);
        void p.then((ok) => {
          grants.current.motion = ok;
          advance();
        });
        return;
      }
      case 'camera': {
        setBusy(true);
        // start() calls getUserMedia before its first await, so the gesture holds.
        void camera
          .start('environment')
          .then((ok) => {
            // start() reports whether a stream is genuinely live. It used to
            // swallow every error, so this was true for a refused camera.
            grants.current.camera = ok;
            advance();
          })
          .catch(() => {
            grants.current.camera = false;
            advance();
          });
        return;
      }
      case 'watcher': {
        grants.current.watcher = true;
        advance();
        return;
      }
      default:
        advance();
    }
  };

  const refuse = () => {
    if (busy) return;
    if (beat.grant === 'watcher') grants.current.watcher = false;
    advance();
  };

  return (
    <div className="rite">
      <Sun
        presence={presence}
        gain={1}
        sizeFraction={1.5}
        centreY={0.38}
        blinkEvery={5.5}
        blinkNow={blinkNow}
      />
      <div className="rite-scrim" />

      {entry !== 'idle' && (
        <div className="rite-body">
          {entry === 'loading' ? (
            <p className="voice-line current" role="status" aria-live="polite">
              I am finding my footing in this room.
              <span className="voice-caret" />
            </p>
          ) : (
            <>
              <p className="voice-line current" role="alert">
                Something would not open. The room is still here; I could not find my footing in it.
              </p>
              <div className="rite-sigil-wrap">
                <button className="sigil" onClick={onRetry} aria-label="try again">
                  <Sigil />
                  <span className="sigil-label">try again</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="rite-body" style={entry !== 'idle' ? { display: 'none' } : undefined}>
        <Voice
          key={beat.id}
          lines={beat.lines}
          startDelayMs={index === 0 ? OPENING_HOLD_MS : 400}
          onComplete={() => setSpoken(true)}
        />

        {spoken && beat.disclosure && <p className="rite-disclosure">{beat.disclosure}</p>}

        {spoken && (
          <div className="rite-sigil-wrap">
            <button
              className={busy ? 'sigil busy' : 'sigil'}
              onClick={touch}
              aria-label={beat.sigil ?? 'continue'}
            >
              <Sigil />
              <span className="sigil-label">{beat.sigil}</span>
            </button>
            {beat.optional && (
              <button className="rite-refuse" onClick={refuse}>
                not this
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** The mark that is touched. Drawn, not typed, so it reads as a seal. */
function Sigil() {
  return (
    <svg viewBox="0 0 64 64" className="sigil-mark" aria-hidden="true">
      <circle cx="32" cy="32" r="26" />
      <circle cx="32" cy="32" r="18" />
      <path d="M32 6 L32 58 M6 32 L58 32" />
      <circle cx="32" cy="32" r="4.5" className="sigil-pupil" />
    </svg>
  );
}
